import React, { memo, useCallback } from 'react'

import {
  bool, func, number, oneOfType, string,
} from 'prop-types'

import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import { NOOP } from 'Utils/stableDefaults'

import Actions from './Components/Actions/Actions'

// The image sits in its own button so every action is one clean focusable
// control with no nested interactives. The hover overlays and actions render as
// siblings; `:focus-within` in the SCSS reveals them for keyboard users too.
function Card({
  id,
  assetId = null,
  slug = null,
  title,
  src,
  aspectRatio,
  eager = false,
  isAdmin = false,
  onOpen = NOOP,
  onEdit = NOOP,
  onDownload = NOOP,
  onGenerate3d = NOOP,
  onHide = NOOP,
  onDelete = NOOP,
}) {
  const t = useTranslation()

  const handleOpen = useCallback(() => onOpen({ id, slug, title, src }), [onOpen, id, slug, title, src])
  const handleEdit = useCallback(
    () => onEdit({ id, assetId, slug, title, src }),
    [onEdit, id, assetId, slug, title, src],
  )
  const handleDownload = useCallback(() => onDownload({ id, title, src }), [onDownload, id, title, src])
  const handleGenerate3d = useCallback(
    () => onGenerate3d({ id, assetId, title, src }),
    [onGenerate3d, id, assetId, title, src],
  )
  const handleHide = useCallback(() => onHide({ id, title }), [onHide, id, title])
  const handleDelete = useCallback(() => onDelete({ id, title }), [onDelete, id, title])

  return (
    <div className="image-gallery-card" style={{ aspectRatio }}>
      <button
        type="button"
        className="image-gallery-card__open"
        onClick={handleOpen}
        aria-label={t('imageGallery.card.open', { title })}
      >
        <img
          className="image-gallery-card__image"
          src={src}
          alt=""
          aria-hidden="true"
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          fetchpriority={eager ? 'high' : 'low'}
        />
      </button>
      <span className="image-gallery-card__overlay image-gallery-card__overlay--top" aria-hidden="true" />
      <span className="image-gallery-card__overlay image-gallery-card__overlay--bottom" aria-hidden="true" />
      <Actions
        id={id}
        title={title}
        isAdmin={isAdmin}
        onEdit={handleEdit}
        onDownload={handleDownload}
        onGenerate3d={handleGenerate3d}
        onHide={handleHide}
        onDelete={handleDelete}
      />
    </div>
  )
}

Card.propTypes = {
  id: oneOfType([number, string]).isRequired,
  assetId: number,
  slug: string,
  title: string.isRequired,
  src: string.isRequired,
  aspectRatio: number.isRequired,
  eager: bool,
  isAdmin: bool,
  onOpen: func,
  onEdit: func,
  onDownload: func,
  onGenerate3d: func,
  onHide: func,
  onDelete: func,
}

export default memo(Card)
